import { useMutation } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";
import { useNavigate } from "react-router-dom";

import { ROUTES } from "@/constants/routes";
import { instance } from "@/lib/axios/instance";
import { ForgotPasswordRequest } from "@/types/request/ForgotPasswordRequest";
import { ForgotPasswordErrorResponse } from "@/types/response/ForgotPasswordResponse";

/**
 * Hook to handle forgot password mutation.
 * Navigates to link sent page after successful submission.
 *
 * @returns Mutation object for forgot password operation
 *
 * @example
 * ```tsx
 * const forgotPasswordMutation = useForgotPasswordMutation();
 * forgotPasswordMutation.mutate({ email: "user@example.com" });
 * ```
 */
export function useForgotPasswordMutation() {
  const navigate = useNavigate();

  return useMutation<
    AxiosResponse,
    AxiosError<ForgotPasswordErrorResponse>,
    ForgotPasswordRequest
  >({
    mutationFn: (resetPasswordRequest: ForgotPasswordRequest) =>
      instance.post("/api/v2/Auth/forgot-password", resetPasswordRequest),
    onSuccess: () => {
      navigate(ROUTES.linkSent);
    },
  });
}
